import { IView, ScrollBar, TextField, LoadingProgress, Checkbox } from '@framework-controls/core';
import * as css from './VerifyIdentity.module.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { strings } from 'src/localization';
import SVPButton from '../SVPButtons/SVPButton';
import { ComponentMapIds } from '../map';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { ApptMasked, VehicleRec, ascCtaInteractionInput, ascPageviewInput } from 'src/models';
import { ascCtaInteraction, ascPageview } from 'src/helpers/EventHelper/EventHelper';

interface IProps {
    sentSecurityCode: boolean;
    errMsg: string | undefined;
    identityVerified: boolean;
    verifyingIdentity: boolean;
    nameId: string;
    login: boolean;
    selectedVehicle: string | undefined;
    mainPaneViewOrder: IView[];
    vehicleList: VehicleRec[];
    serviceDocsFlag: string;
    sendSecurityCode: (contact: string) => void;
    verifyIdentity: (securityCode: string, rememberMe: boolean) => void;
    popFromMainPane: (id: string) => void;
    contactMethods: (string | undefined)[];
    clearError: () => void;
    updateMaskedFields: (inp: ApptMasked) => void;
    getVehicles: () => void;
    selectVehicle: (inp: string) => void;
    getExCustomer: (inp: string) => void;
    fetchROs: (id: string) => void;
    getExCust: (nameId: string) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    theme?: string;
    locale: string;
    setVerifyingIdentity: (inp: boolean) => void;
}

const VerifyIdentity = (props: IProps) => {
    const [securityCode, setSecurityCode] = useState('');
    const [codeError, setCodeError] = useState(false);
    const [codeHelperTxt, setCodeHelperTxt] = useState('');
    const [disableVerify, setDisableVerify] = useState(true);
    const [verifyingIdentity, setVerifyingIdentity] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const currentScreen = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].component;
    const baseScreen = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 2]?.component;
    const noArrow = props.theme === 'porsche' || props.theme === 'bmw' ? true : false;

    // This logic is just to force a rebuild if locale changes.  If should not really do anything.
    useEffect(() => {
        if (props.locale === 'xx') {
            // setForgotCSS(css.passLitAccess);
        }
    }, [props.locale]);

    let subHeader = strings.SecurityString1;
    if (baseScreen === ComponentMapIds.Login) {
        subHeader += strings.SecurityString2;
    } else if (baseScreen === ComponentMapIds.ServiceDocs) {
        subHeader += strings.SecurityString4;
    } else {
        subHeader += strings.SecurityString3;
    }

    useEffect(() => {
        if (securityCode?.length === 6) {
            setDisableVerify(false);
        } else {
            setDisableVerify(true);
        }
    }, [securityCode]);

    const securityCodeHandler = useCallback((id: string, value: string) => {
        const securityCodeRegex = new RegExp(/^[0-9]$/.source);
        const updateValue = value?.length < 7 && (!!value[value.length - 1]?.match(securityCodeRegex) || value === '');
        if (updateValue) {
            setSecurityCode(value);
        }
    }, []);

    const codeErrorHandler = useCallback(() => {
        if (securityCode.length < 6) {
            setCodeError(true);
            setCodeHelperTxt(strings.CodeError);
        } else {
            setCodeError(false);
            setCodeHelperTxt('');
        }
    }, [securityCode]);

    const verifyIdentity = useCallback((e) => {
        setVerifyingIdentity(true);
        props.setVerifyingIdentity(true);
        props.verifyIdentity(securityCode, rememberMe);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Verify,
            elementColor: '',
            elementOrder: '',
            elementType: 'form',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [securityCode, props.verifyIdentity, rememberMe, props.selectedVehicle, props.vehicleList, props.setVerifyingIdentity]);

    const keyFunction = useCallback((e) => {
        if (e.charCode === 13) {
            verifyIdentity(e);
        }
    }, [verifyIdentity]);

    const contactHandler = useCallback((contact: string, contactIndex: string, e: any) => {
        props.sendSecurityCode(contactIndex);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: contact,
            elementColor: '',
            elementOrder: '',
            elementType: 'form',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props.sendSecurityCode, props.selectedVehicle, props.vehicleList]);

    const contactList = props.contactMethods.map((contact, index) => {
        if (contact) {
            // need to use a localization string for translation
            let btnMessage = `${strings.PhoneHeader} - ${contact}`;
            let contactIndex = index.toString();
            // If an email was selected, we set the contactIndex to -1,
            // which is used to select the last value of the contact order list.
            if (contact.includes('@')) {
                btnMessage = `${strings.EmailHeader} - ${contact}`;
                contactIndex = '-1';
            }
            const button = (
                <div className={css.contactBtnDiv} key={contact}>
                    <SVPButton
                        id={contact}
                        message={btnMessage}
                        handler={(e: any) => contactHandler(contact, contactIndex, e)}
                        noArrow
                        variant={'MFABtn'}
                        theme={props.theme}
                    />
                </div>);

            return button;
        }

        return null;
    });

    const closeHandler = useCallback((e: any) => {
        props.clearError();
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.RememberDevice,
            elementColor: '',
            elementOrder: '',
            elementType: 'form',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props.clearError, props.vehicleList, props.selectedVehicle]);

    useEffect(() => {
        const isPreviousPageLogin =
            props.mainPaneViewOrder[props.mainPaneViewOrder.length - 2]?.component === ComponentMapIds.Login;
        const onThisPage = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].component === ComponentMapIds.VerifyIdentity;
        if (props.identityVerified && (!props.verifyingIdentity || !isPreviousPageLogin) && onThisPage) {
            const maskedFieldInp = {
                firstName: false,
                lastName: false,
                phone: false,
                email: false,
            };
            props.updateMaskedFields(maskedFieldInp);
            if (!props.login) {
                props.getExCust(props.nameId);
            }

            props.popFromMainPane(ComponentMapIds.VerifyIdentity);

            return;
        }
    }, [props.identityVerified,
        props.popFromMainPane,
        props.updateMaskedFields,
        props.getVehicles,
        props.verifyingIdentity,
        props.fetchROs,
        props.nameId,
        props.getExCust,
        props.login,
        props.mainPaneViewOrder,
    ]);

    useEffect(() => {
        if (verifyingIdentity &&
            props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].component !== ComponentMapIds.VerifyIdentity) {
            setVerifyingIdentity(false);
        }
    }, [props.identityVerified, verifyingIdentity, props.errMsg, props.mainPaneViewOrder]);

    useEffect(() => {
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.VerifyIdentity) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicleList, props.selectedVehicle);
        }
    }, [props.mainPaneViewOrder, props.vehicleList, props.selectedVehicle]);

    const checkboxHandler = useCallback((e: any) => {
        setRememberMe(!rememberMe);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.RememberDevice,
            elementColor: '',
            elementOrder: '',
            elementType: 'form',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: rememberMe ? 'uncheck' : 'check',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [rememberMe, props.vehicleList, props.selectedVehicle]);

    const error = useMemo(() => {
        if (
            props.errMsg &&
            props.errMsg !== '0000' &&
            props.errMsg !== '1004' &&
            currentScreen === ComponentMapIds.VerifyIdentity
        ) {
            setVerifyingIdentity(false);

            return GetErrorMessage(props.errMsg, closeHandler);
        }

        return null;
    }, [currentScreen, props.errMsg, closeHandler]);

    useEffect(() => {
        if (!props.serviceDocsFlag) {
            props.addLogEntry('Screen Entry', 'Verify Identity', '');
        }
    }, [props.addLogEntry, props.serviceDocsFlag]);

    return (
        <ScrollBar scrollVertical>
            <div className={css.body} id='body'>
                {error}
                {verifyingIdentity && props.errMsg !== '1001' ?
                    <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> :
                    null
                }
                <div className={css.topContainer}>
                    <h2 className={props.theme === 'mazda' && css.headerMazda}>{strings.IdentityVerification}</h2>
                    <h3>{subHeader}</h3>
                    <hr />
                </div>
                <div className={css.bottomContainer} >
                    <h3>{strings.SelectAuthMethod}</h3>
                    <div id={'contactList'} className={css.contactList}>
                        {contactList}
                    </div>
                    <div id='codeSent' className={css.codeSent}>
                        {props.sentSecurityCode ? strings.CodeSent : null}
                    </div>
                    <div className={css.Input2}>
                        <TextField
                            id='password'
                            type='integer'
                            label={strings.SecurityCode}
                            value={securityCode}
                            size='full'
                            onChange={securityCodeHandler}
                            error={codeError}
                            onBlur={codeErrorHandler}
                            helperText={codeHelperTxt}
                            onKeyPress={(e) => keyFunction(e)}
                        />
                    </div>
                    <div className={css.rememberMe}>
                        <Checkbox
                            id='rememberMe'
                            checked={rememberMe}
                            size='rg'
                            labelClass={css.rememberMeChkBox}
                            label={strings.RememberDevice}
                            labelPlacement='end'
                            onChange={checkboxHandler}
                        />
                    </div>
                    <div className={css.passBtnDiv}>
                        <SVPButton
                            id='verifyBtn'
                            message={strings.Verify}
                            handler={verifyIdentity}
                            isDisabled={disableVerify}
                            noArrow={noArrow}
                            backArrow={false}
                            theme={props.theme}
                        />
                    </div>
                </div>
            </div>
        </ScrollBar>

    );
};

function GetErrorMessage(message: string, closeHandler: (e: any) => void) {
    let headerMsg = '';
    let errorMsg = '';
    const primaryMessage = strings.TryAgain;
    const secondaryMessage = '';
    const primaryHandler = closeHandler;
    const secondaryHandler = () => '';

    switch (message) {
        case '1001':
            headerMsg = strings.IncorrectCode;
            errorMsg = strings.EnterCodeAgain;
            break;
        case '1002':
            headerMsg = strings.ExpiredCode;
            errorMsg = strings.RequestNewCode;
            break;
        case '1003':
            headerMsg = strings.InvalidCode;
            errorMsg = strings.MaxAttemptsExceeded;
            break;
        default:
            headerMsg = strings.InvalidCode;
            errorMsg = strings.RequestOrContact;
            break;
    }

    return (
        <ErrorMessage
            headerMsg={headerMsg}
            errorMsg={errorMsg}
            primaryBtnMessage={primaryMessage}
            secondaryBtnMessage={secondaryMessage}
            primaryHandler={(e: any) => primaryHandler(e)}
            secondaryHandler={secondaryHandler}
            closeHandler={(e: any) => closeHandler(e)}
        />
    );
}

export default VerifyIdentity;